import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, computed, effect, signal } from '@angular/core';
import { FloChatService } from '../../../services/flo-chat.service';
import { FloOverlayService } from '../../../services/flo-overlay.service';
import { FloPriority, prioritySignatureOf, highRiskLabel } from './flo-priority.util';
import { lsGet, lsSet } from './flo-storage.util';
import { FloSparkIconComponent } from './flo-spark-icon.component';

/**
 * Flô's floating action button. Two attention behaviors, per browser:
 *
 * - the pulse is first-contact only — it stops for good after the user opens the chat once;
 * - the speech-bubble callout (real priority count, 2s→8s mockup timing) announces each NEW
 *   priority set exactly once, chatting history notwithstanding — the stored signature
 *   (shared with the insight banner) decides what counts as news.
 */
@Component({
	selector: 'app-flo-fab',
	standalone: true,
	imports: [FloSparkIconComponent],
	changeDetection: ChangeDetectionStrategy.OnPush,
	template: `
		@if (!flo.expanded()) {
			<div class="flo-fab-wrap">
				@if (calloutVisible()) {
					<button class="flo-fab-callout" type="button" (click)="openChat()">
						<strong>A Flô:</strong>
						{{ calloutText() }}
						<span class="flo-fab-callout__tail"></span>
					</button>
				}
				<button class="flo-fab" type="button"
				        [class.flo-fab--calm]="hasOpenedBefore()" (click)="openChat()"
				        title="Conversar com a Flô" aria-label="Abrir chat da Flô">
					<img class="flo-fab__avatar" src="assets/images/flo/flo-avatar.webp" alt="Flô" />
					<span class="flo-fab__status"></span>
					<span class="flo-fab__spark">
						<app-flo-spark-icon [size]="10" [strokeWidth]="2.4" />
					</span>
				</button>
			</div>
		}
	`,
	styleUrls: ['./flo-fab.component.scss']
})
export class FloFabComponent implements OnInit, OnDestroy {
	private static readonly OPENED_KEY = 'flo.fab.opened';
	private static readonly CALLOUT_SIG_KEY = 'flo.fab.calloutSig';
	private static readonly CALLOUT_SHOW_MS = 2000;
	private static readonly CALLOUT_HIDE_MS = 8000;

	readonly hasOpenedBefore = signal(lsGet(FloFabComponent.OPENED_KEY) === '1');
	readonly calloutVisible = signal(false);
	/** Full callout sentence; reads naturally even when there are no high-risk areas. */
	readonly calloutText = computed(() => {
		const n = this.flo.highRiskCount();
		return n > 0 ? `seu relatório tem ${highRiskLabel(n)} — quer ver?` : 'seu relatório está pronto — quer ver?';
	});
	private showTimer: ReturnType<typeof setTimeout> | null = null;
	private hideTimer: ReturnType<typeof setTimeout> | null = null;
	private triggerElement: Element | null = null;

	constructor(public flo: FloChatService, private floOverlay: FloOverlayService) {
		// React once the shared priorities resolve (fetched a single time for the whole page).
		// An empty set schedules nothing; a failed fetch publishes [] — either way, no callout.
		effect(() => {
			const priorities = this.flo.priorities();
			if (priorities) this.scheduleCallout(priorities);
		});
		// Return focus to the FAB trigger (WCAG 2.1 SC 2.1.2) when the panel is closed.
		effect(() => {
			if (!this.flo.expanded() && this.triggerElement instanceof HTMLElement) {
				this.triggerElement.focus();
				this.triggerElement = null;
			}
		});
	}

	ngOnInit(): void {
		this.flo.loadPriorities();
		this.flo.loadPills(); // prefetch the chat pills so they're ready the moment the user opens Flô
		void this.flo.ensureDisclaimerStatus(); // prefetch first-use status so the panel opens without a flash
	}

	openChat(): void {
		if (typeof document !== 'undefined') {
			this.triggerElement = document.activeElement;
		}
		lsSet(FloFabComponent.OPENED_KEY, '1');
		this.hasOpenedBefore.set(true);
		this.calloutVisible.set(false);
		this.flo.expand();
		this.floOverlay.showPanel();
	}

	private scheduleCallout(priorities: FloPriority[]): void {
		if (priorities.length === 0) return;
		const signature = prioritySignatureOf(priorities);
		if (lsGet(FloFabComponent.CALLOUT_SIG_KEY) === signature) return;

		// Clear any pending pair before scheduling a new one (idempotent, prevents double timers)
		if (this.showTimer !== null) { clearTimeout(this.showTimer); this.showTimer = null; }
		if (this.hideTimer !== null) { clearTimeout(this.hideTimer); this.hideTimer = null; }

		this.showTimer = setTimeout(() => {
			lsSet(FloFabComponent.CALLOUT_SIG_KEY, signature);
			this.calloutVisible.set(true);
		}, FloFabComponent.CALLOUT_SHOW_MS);
		this.hideTimer = setTimeout(() => this.calloutVisible.set(false), FloFabComponent.CALLOUT_HIDE_MS);
	}

	ngOnDestroy(): void {
		if (this.showTimer !== null) { clearTimeout(this.showTimer); this.showTimer = null; }
		if (this.hideTimer !== null) { clearTimeout(this.hideTimer); this.hideTimer = null; }
	}
}
